<!--
  One control, as the designer has it: a + circle, then the top reactions as overlapping
  circles and the total of every reaction — or, with none yet, a grey smiley circle beside
  the +. Hovering the + opens the palette; a click (or tap) ANYWHERE on the control does too,
  and the palette is also where you take a reaction back off. Hovering the reactions or the
  total shows the breakdown: every reaction on the post, with who left it — the circles only
  have room for three.

  The breakdown sits beside the button, not inside it — its avatars are links, and
  interactive content can't nest inside a <button>.
-->
<li class="list-inline-item reaction-control position-relative" (mouseleave)="onControlMouseLeave()">
  <div class="reaction-control__main">
    <button
      type="button"
      class="reaction-control__summary"
      [class.reaction-control__summary--open]="reactionBarOpen"
      [attr.aria-label]="summaryLabel"
      [attr.aria-expanded]="reactionBarOpen"
      aria-haspopup="listbox"
      (click)="onMainClick($event)"
      (keydown)="onMainKeydown($event)"
      (pointerdown)="onPointerDown($event)"
      (pointerup)="onPointerUp()"
      (pointerleave)="onPointerLeave()"
      (pointercancel)="onPointerUp()"
    >
      <span class="reaction-control__bubble reaction-control__bubble--add" aria-hidden="true" (mouseenter)="onAddHover()">
        <fa-icon icon="plus"></fa-icon>
      </span>
      <!-- Empty state: a grey OUTLINE smiley says what the + adds. An outline, not a colour
           emoji, so it doesn't read as a reaction someone already left. -->
      @if (!reactionTotal) {
        <span class="reaction-control__bubble reaction-control__bubble--face" aria-hidden="true">
          <fa-icon [icon]="smileIcon"></fa-icon>
        </span>
      }
      <!-- The first reaction sits clear of the +; each after it tucks under its neighbour. -->
      @if (reactionTotal) {
        <span class="reaction-control__stack" (mouseenter)="onStackEnter()" (mouseleave)="scheduleHideReactors()">
          @for (item of topReactions; track item.type; let first = $first) {
            <span class="reaction-control__bubble" [class.reaction-control__bubble--stacked]="!first" aria-hidden="true">{{ item.emoji }}</span>
          }
          <span class="reaction-control__total" [class.reaction-control__total--mine]="myReactions.length" aria-hidden="true">{{ reactionTotal }}</span>
        </span>
      }
    </button>

    @if (reactionBarOpen) {
      <div
        class="reaction-control__bar"
        [class.reaction-control__bar--right]="barAlignRight"
        [style.left.px]="barLeft"
        [style.max-width.px]="barMaxWidth"
        role="listbox"
        aria-label="Choose a reaction"
        (mouseenter)="onBarEnter()"
        (keydown)="onBarKeydown($event)"
      >
        @for (type of reactionTypes; track type; let idx = $index) {
          <button
            type="button"
            class="reaction-control__option"
            [class.selected]="myReactionSet[type]"
            [class.focused]="focusedReactionIndex === idx"
            role="option"
            [attr.aria-selected]="myReactionSet[type]"
            [attr.aria-label]="reactionLabels[type]"
            [attr.tabindex]="focusedReactionIndex === idx ? 0 : -1"
            (click)="selectReaction(type); $event.stopPropagation()"
            (mouseenter)="focusedReactionIndex = idx"
          >
            <span aria-hidden="true">{{ reactionEmoji[type] }}</span>
          </button>
        }
      </div>
    }
  </div>

  <!-- Every reaction, most used first: the emoji, how many, and who. Reactors without a
       resolvable photo get the initials-on-tint fallback comment.component uses for authors. -->
  @if (shouldShowReactorsList && breakdown.length) {
    <div
      class="reaction-control__breakdown"
      [class.reaction-control__breakdown--right]="reactorsAlignRight"
      [style.left.px]="reactorsLeft"
      role="group"
      aria-label="Reactions"
      (mouseenter)="keepReactors()"
      (mouseleave)="scheduleHideReactors()"
    >
      <!-- Outside the panel, which scrolls and would clip it. -->
      @if (activeHoveringReactorName) {
        <div class="reaction-control__reactor-name">{{ activeHoveringReactorName }}</div>
      }
      <div class="reaction-control__breakdown-panel" [style.max-width.px]="reactorsMaxWidth" [style.min-width.px]="reactorsMaxWidth === null ? null : 0">
        <ul class="reaction-control__breakdown-list">
          @for (row of breakdown; track row.type) {
            <li class="reaction-control__breakdown-row" [class.reaction-control__breakdown-row--mine]="row.mine">
              <span class="reaction-control__breakdown-emoji" aria-hidden="true">{{ row.emoji }}</span>
              <span class="reaction-control__breakdown-count">
                {{ row.count }}
                <span class="sr-only">{{ row.label }}{{ row.mine ? ", including you" : "" }}</span>
              </span>
              <ul class="reaction-control__avatars">
                @for (id of row.userIds; track id) {
                  <li
                    class="reaction-control__avatar"
                    [class.reaction-control__avatar--initials]="!reactorHasPhoto[id]"
                    role="link"
                    tabindex="0"
                    [ngStyle]="reactorHasPhoto[id] ? reactorPhotoStyles[id] : { 'background-color': reactorColors[id] }"
                    (mouseenter)="reactorMouseEnter(id)"
                    (mouseleave)="reactorMouseLeave()"
                    (focus)="reactorMouseEnter(id)"
                    (click)="onViewProfile(id)"
                    (keydown.enter)="onViewProfile(id)"
                  >
                    @if (!reactorHasPhoto[id]) {
                      <span aria-hidden="true">{{ reactorInitials[id] }}</span>
                    }
                    <span class="sr-only">go to {{ activeHoveringReactorNameList[id] ?? "user" }} profile</span>
                  </li>
                }
                @if (row.more) {
                  <li class="reaction-control__avatars-more">+{{ row.more }}</li>
                }
              </ul>
            </li>
          }
        </ul>
      </div>
    </div>
  }
</li>
